import {
  Component, OnInit, inject, signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, SlicePipe } from '@angular/common';
import { Router, RouterLink } from '@angular/router';

import { ActiveTenantService } from '../../../core/services/active-tenant.service';

/** Topbar tenant-switcher dropdown; hidden when the caller belongs to zero or one tenant. */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-tenant-switcher',
  standalone: true,
  imports: [CommonModule, RouterLink, SlicePipe],
  templateUrl: './tenant-switcher.component.html',
})
export class TenantSwitcherComponent implements OnInit {
  protected activeTenantService = inject(ActiveTenantService);
  private readonly router = inject(Router);

  protected open       = signal(false);
  protected switching  = signal<string | null>(null);

  ngOnInit(): void {
    if (!this.activeTenantService.loaded()) this.activeTenantService.loadTenants();
  }

  toggle(): void { this.open.update((v) => !v); }
  close():  void { this.open.set(false); }

  /** Fixed 14-day recovery window (docs/adr/0008) - not tier-scaled, so no config to read it from. */
  protected daysRemaining(deletedAt: string): number {
    const elapsedMs = Date.now() - new Date(deletedAt).getTime();
    const remaining = 14 - Math.floor(elapsedMs / 86_400_000);
    return Math.max(0, remaining);
  }

  onBackdropClick(e: Event): void { if (e.target === e.currentTarget) this.close(); }

  async pick(id: string): Promise<void> {
    if (id === this.activeTenantService.activeId()) { this.close(); return; }
    this.switching.set(id);
    try {
      this.close();
      await this.activeTenantService.switch(id);
    } finally {
      this.switching.set(null);
    }
  }

  /**
   * A soft-deleted tenant can't just be "switched into" the way an active
   * one is - every deletion-management endpoint (accept/cancel/status/
   * restore) requires the tenant to already be the caller's active tenant
   * (backend's RequireTenantIDMatchesActive), so its restore option is
   * unreachable without switching first. Switch and land on Settings in one
   * hard navigation: routing there first and switching after would leave the
   * URL pointing at a tenant that isn't active yet, which every
   * tenant-scoped call answers with 403 resource_not_in_active_tenant.
   */
  async pickDeleted(id: string): Promise<void> {
    this.close();
    await this.activeTenantService.switch(id, `/tenants/${id}/settings`);
  }
}
